import { Injectable } from '@angular/core';
import { AuthUser } from 'app/core/models/auth_user.model';
import { UserService } from 'app/shared/services/user.service';
import { Subsidiary } from 'app/types/subsidiary.type';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { StorageIntegrityService } from 'app/shared/security/storage-integrity.service';
import { SubsidiaryService } from 'app/services/subsidiary/subsidiary.service';
import { LocalStorageService } from 'app/shared/services/local-storage.service';
import { EnvironmentService } from 'app/services/environment.service';

export interface SubsidiaryContext {
    selectedSubsidiary: Subsidiary | null;
    subsidiaries: Subsidiary[];
    canSelect: boolean;
    /**
     * `true` desde que el servicio terminó de armar el contexto al menos una
     * vez, haya o no sucursales. El valor inicial del `BehaviorSubject` no lo
     * trae: es la hidratación en curso, y se emite sincrónicamente a todo el
     * que se suscribe, así que no se puede distinguir de "resuelto sin
     * sucursales" mirando sólo las listas.
     */
    resolved?: boolean;
}

/**
 * El contexto ya terminó de hidratarse: no hace falta esperar otra emisión
 * para saber si hay sucursal elegida.
 *
 * Un contexto con sucursales cuenta como resuelto aunque no traiga `resolved`:
 * ya tiene entre qué elegir.
 */
export function contextoResuelto(
    contexto: SubsidiaryContext | null | undefined
): boolean {
    return contexto?.resolved === true || (contexto?.subsidiaries?.length ?? 0) > 0;
}

const DEFAULT_CONTEXT: SubsidiaryContext = {
    selectedSubsidiary: null,
    subsidiaries: [],
    canSelect: false,
};

@Injectable({
    providedIn: 'root',
})
export class SubsidiaryContextService {
    private static readonly STORAGE_KEY = 'selectedSubsidiaryId';
    private static readonly ADMIN_ROLES = ['admin', 'superadmin', 'fab_admin', 'admin_retail_user'];

    private readonly _context$ = new BehaviorSubject<SubsidiaryContext>(
        DEFAULT_CONTEXT,
    );

    private readonly _hydratingUsers = new Set<string>();

    constructor(
        private _userService: UserService,
        private _storageIntegrity: StorageIntegrityService,
        private _subsidiaryService: SubsidiaryService,
        private _localStorage: LocalStorageService,
        private _environmentService: EnvironmentService,
    ) {
        this._userService.user$.subscribe((user) => {
            void this.updateContext(user);
        });
    }

    get context$(): Observable<SubsidiaryContext> {
        return this._context$.asObservable();
    }

    get selectedSubsidiary(): Subsidiary | null {
        return this._context$.value.selectedSubsidiary;
    }

    get subsidiaries(): Subsidiary[] {
        return this._context$.value.subsidiaries;
    }

    get canSelect(): boolean {
        return this._context$.value.canSelect;
    }

    selectSubsidiary(subsidiaryId: number | null): void {
        if (subsidiaryId == null) {
            void this.persistSelectedSubsidiary(null);
            this._context$.next({
                ...this._context$.value,
                selectedSubsidiary: null,
            });
            return;
        }

        const selected = this._context$.value.subsidiaries.find(
            (subsidiary) => subsidiary.id === subsidiaryId,
        );

        if (!selected) {
            return;
        }

        void this.persistSelectedSubsidiary(subsidiaryId);

        this._context$.next({
            ...this._context$.value,
            selectedSubsidiary: selected,
        });
    }

    async clearContext(): Promise<void> {
        await this.persistSelectedSubsidiary(null);
        this._context$.next(DEFAULT_CONTEXT);
    }

    async refreshContext(): Promise<void> {
        const currentUser = this._userService.user;

        if (!currentUser) {
            return;
        }

        await this.updateContext(currentUser);
    }

    private async updateContext(user: AuthUser | undefined): Promise<void> {
        let secureToken = await this._storageIntegrity.getSecureData<string>(
            'accessToken',
            { skipValidation: true },
        );

        if (!secureToken) {
            const legacyToken = (this._localStorage.accessToken || '').trim();
            secureToken = legacyToken ? legacyToken : null;
        }

        if (!user || !secureToken) {
            void this.persistSelectedSubsidiary(null);
            this._context$.next({ ...DEFAULT_CONTEXT, resolved: true });
            return;
        }

        let availableSubsidiaries = Array.isArray(user.subsidiaries)
                ? this.sortSubsidiaries(user.subsidiaries.filter(Boolean))
            : [];
        if (SubsidiaryContextService.ADMIN_ROLES.includes(user.role)) {
            const hydrated = await this.fetchSubsidiariesForUser(user);
            if (hydrated.length > 0) {
                    availableSubsidiaries = this.sortSubsidiaries(hydrated);
            }
        }

        const canSelect =
            availableSubsidiaries.length > 0 &&
            SubsidiaryContextService.ADMIN_ROLES.includes(user.role);

        const storedId = await this.getPersistedSubsidiaryId();
        const fallback = availableSubsidiaries[0] ?? null;

        const selected = availableSubsidiaries.find((subsidiary) => subsidiary.id === storedId) ?? fallback;

        if (selected) {
            void this.persistSelectedSubsidiary(selected.id);
        } else {
            void this.persistSelectedSubsidiary(null);
        }

        this._context$.next({
            selectedSubsidiary: selected,
            subsidiaries: availableSubsidiaries,
            canSelect,
            resolved: true,
        });
    }

    private async fetchSubsidiariesForUser(user: AuthUser | undefined): Promise<Subsidiary[]> {
        if (!user?.id) {
            return [];
        }

        if (this._hydratingUsers.has(user.id)) {
            return [];
        }

        this._hydratingUsers.add(user.id);

        try {
            const response = await firstValueFrom(this._subsidiaryService.getAll());
            const subsidiaries = Array.isArray(response?.data)
                    ? this.sortSubsidiaries(response.data.filter(Boolean))
                    : [];

            if (subsidiaries.length === 0) {
                return [];
            }

            const updatedUser = AuthUser.createOne(
                {
                    ...user.toJson(),
                    subsidiaries,
                },
                new AuthUser(),
            );

            this._userService.user = updatedUser;

            const serializedUser = JSON.stringify(updatedUser.toJson());
            await this._storageIntegrity.setSecureData('user', serializedUser);
            this._localStorage.user = serializedUser;

            return subsidiaries;
        } catch (error) {
            if (this._environmentService.isDevelopment()) {
                console.warn('[SubsidiaryContext] Failed to hydrate subsidiaries from API', error);
            }

            return [];
        } finally {
            this._hydratingUsers.delete(user.id);
        }
    }

        private sortSubsidiaries(subsidiaries: Subsidiary[]): Subsidiary[] {
            return [...subsidiaries].sort((a, b) => {
                const codeA = a?.code ?? '';
                const codeB = b?.code ?? '';

                if (codeA && codeB) {
                    const codeCompare = codeA.localeCompare(codeB, undefined, {
                        numeric: true,
                        sensitivity: 'base',
                    });

                    if (codeCompare !== 0) {
                        return codeCompare;
                    }
                } else if (codeA && !codeB) {
                    return -1;
                } else if (!codeA && codeB) {
                    return 1;
                }

                const nameA = a?.fantasyName ?? '';
                const nameB = b?.fantasyName ?? '';

                return nameA.localeCompare(nameB, undefined, {
                    sensitivity: 'base',
                });
            });
        }

    private async getPersistedSubsidiaryId(): Promise<number | null> {
        if (typeof window === 'undefined') {
            return null;
        }

        try {
            const storedValue = await this._storageIntegrity.getSecureData<number>(
                SubsidiaryContextService.STORAGE_KEY,
            );

            if (storedValue != null) {
                const parsed = Number(storedValue);
                return Number.isNaN(parsed) ? null : parsed;
            }

            const legacyValue = window.localStorage.getItem(
                SubsidiaryContextService.STORAGE_KEY,
            );

            if (!legacyValue) {
                return null;
            }

            const parsedLegacy = Number(legacyValue);

            if (Number.isNaN(parsedLegacy)) {
                this.removeLegacyStorageKey();
                return null;
            }

            await this.persistSelectedSubsidiary(parsedLegacy);

            return parsedLegacy;
        } catch (error) {
            console.warn('[SubsidiaryContext] Failed to read stored value', error);
            return null;
        }
    }

    private async persistSelectedSubsidiary(
        subsidiaryId: number | null,
    ): Promise<void> {
        if (typeof window === 'undefined') {
            return;
        }

        try {
            if (subsidiaryId == null) {
                this._storageIntegrity.clearSecureData(
                    SubsidiaryContextService.STORAGE_KEY,
                );
                this.removeLegacyStorageKey();
                return;
            }

            await this._storageIntegrity.setSecureData<number>(
                SubsidiaryContextService.STORAGE_KEY,
                subsidiaryId,
                {
                    metadata: {
                        entity: 'subsidiary',
                    },
                },
            );
        } catch (error) {
            console.warn('[SubsidiaryContext] Failed to persist value', error);
        }
    }

    private removeLegacyStorageKey(): void {
        try {
            window.localStorage.removeItem(SubsidiaryContextService.STORAGE_KEY);
        } catch (error) {
            console.warn('[SubsidiaryContext] Failed to remove legacy storage key', error);
        }
    }
}
